import jsPDF from "jspdf"
import "jspdf-autotable"
import { deliverPdf } from "./helpers/pdfDelivery.js"
import { getVendedoresTexto } from "./helpers/vendedoresPdf.js"
import { getPasajeroNombrePdf, sortPasajerosPdf } from "./helpers/pasajerosPdf.js"

const formatCurrency = (value) => {
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    maximumFractionDigits: 0,
  }).format(value)
}

// ------------------------------------------------------------------------------------------------------------------------------------------------------------------------------
// ------------------------------------------------------------------------------------------------------------------------------------------------------------------------------
// ------------------------------------------------------------------------------------------------------------------------------------------------------------------------------
// ------------------------------------------------------------------------------------------------------------------------------------------------------------------------------
// ------------------------------------------------------------------------------------------------------------------------------------------------------------------------------
const resolveSaldo = (pasajero) => {
  const saldoPendiente = pasajero?.montoPendiente
  if (saldoPendiente !== null && saldoPendiente !== undefined && Number.isFinite(Number(saldoPendiente))) {
    return Number(saldoPendiente)
  }
  return 0
}

const resolveAbonado = (pasajero) => {
  const abonado = pasajero?.abonado
  if (abonado !== null && abonado !== undefined && Number.isFinite(Number(abonado))) {
    return Number(abonado)
  }
  return 0
}

const resolveTotal = (pasajero) => {
  const total = pasajero?.total
  if (total !== null && total !== undefined && Number.isFinite(Number(total))) {
    return Number(total)
  }
  return resolveAbonado(pasajero) + resolveSaldo(pasajero)
}

export const generarPdfClientesReceta = ({
  receta,
  pasajerosData,
  logoBase64 = null,
  deliveryContext = null,
  vendedoresHeaderLineas = null,
  showFechaSalida = true,
}) => {
  const doc = new jsPDF({ unit: "pt", format: "a4" })
  const pageWidth = doc.internal.pageSize.getWidth()
  const margin = 30

  const PAL = {
    primary: [25, 118, 210],
    secondary: [120, 144, 156],
    ink: [33, 33, 33],
    muted: [117, 117, 117],
    inkLight: [158, 158, 158],
    bgSoft: [245, 247, 250],
  }

  const sectionTitle = (label, y) => {
    doc.setDrawColor(...PAL.primary)
    doc.setLineWidth(1)
    doc.line(margin, y + 6, pageWidth - margin, y + 6)
    doc.setFont("helvetica", "bold")
    doc.setFontSize(12)
    doc.setTextColor(...PAL.primary)
    doc.text(label, margin, y)
  }

  const getImageFormat = (dataUrl) => {
    const match = /^data:image\/(png|jpe?g|webp);/i.exec(dataUrl || "")
    if (!match) return "PNG"
    const ext = match[1].toLowerCase()
    if (ext === "jpg" || ext === "jpeg") return "JPEG"
    if (ext === "webp") return "WEBP"
    return "PNG"
  }

  // Logo (logoTenant)
  if (logoBase64) {
    try {
      const logoMaxW = 80
      const logoMaxH = 36
      const props = doc.getImageProperties(logoBase64)
      const ratio = Math.min(logoMaxW / props.width, logoMaxH / props.height)
      const logoW = props.width * ratio
      const logoH = props.height * ratio
      const logoX = margin
      const logoY = 12
      doc.addImage(logoBase64, getImageFormat(logoBase64), logoX, logoY, logoW, logoH)
    } catch (error) {
      // Ignorar errores de logo para no bloquear el PDF
    }
  }
  doc.setFont("helvetica", "bold")
  doc.setFontSize(14)
  doc.setTextColor(...PAL.primary)
  doc.text("LISTA DE PASAJEROS", pageWidth / 2, 36, { align: "center" })

  doc.setFont("helvetica", "normal")
  doc.setFontSize(9)
  doc.setTextColor(...PAL.inkLight)
  doc.text(`Fecha: ${new Date().toLocaleDateString("es-AR")}`, pageWidth - margin, 54, { align: "right" })

  doc.text("Vendedor", pageWidth - margin, 66, { align: "right" })
  doc.setTextColor(...PAL.ink)
  const lineas = Array.isArray(vendedoresHeaderLineas) && vendedoresHeaderLineas.length > 0
    ? vendedoresHeaderLineas
    : [getVendedoresTexto(receta)]
  const vendedorBaseY = 78
  lineas.forEach((linea, index) => {
    doc.setFontSize(10)
    doc.text(linea || "-", pageWidth - margin, vendedorBaseY + index * 11, { align: "right" })
  })
  doc.setFontSize(9)
  doc.setTextColor(...PAL.inkLight)

  const grupoTexto = `${receta?.codigoProductoTerminado || "N/A"} - ${receta?.descripcionProductoTerminado || ""}`.trim()
  const destinoTexto = receta?.destino || "N/A"

  doc.setFont("helvetica", "bold")
  doc.setFontSize(10)
  doc.setTextColor(...PAL.ink)
  doc.text(`Grupo: ${grupoTexto}`, margin, 80)
  doc.text(`Destino: ${destinoTexto}`, margin, 96)
  if (showFechaSalida) {
    doc.text(`Fecha viaje: ${receta?.fechaInicio || "N/A"}`, margin, 112)
  }

  const rows = sortPasajerosPdf(pasajerosData).map((pasajero, index) => {
    const nombreCompleto = getPasajeroNombrePdf(pasajero)
    const saldo = resolveSaldo(pasajero)

    return [
      String(index + 1),
      nombreCompleto,
      pasajero?.dniCuitCuil || "-",
      pasajero?.observaciones || "-",
      formatCurrency(saldo),
    ]
  })

  sectionTitle("PASAJEROS", 155)

  doc.autoTable({
    startY: 175,
    margin: { left: margin, right: margin },
    theme: "striped",
    head: [["#", "Pasajero", "DNI/CUIT", "Observaciones", "A Pagar"]],
    body: rows,
    styles: { font: "helvetica", fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: PAL.primary, textColor: [255, 255, 255], fontStyle: "bold" },
    columnStyles: {
      0: { cellWidth: 25, halign: "center" },
      1: { cellWidth: 160 },
      2: { cellWidth: 70 },
      3: { cellWidth: 180 },
      4: { cellWidth: 65, halign: "right" },
    },
  })

  const fileName = `lista_pasajeros_${receta?.codigoProductoTerminado || "grupo"}_${receta?.destino || "destino"}_${new Date()
    .toISOString()
    .slice(0, 10)}.pdf`
  doc.setProperties({ title: fileName, subject: "Lista de Pasajeros", creator: "Sistema" })
  deliverPdf({ doc, fileName, targetWindow: deliveryContext?.targetWindow || null })
}


// ------------------------------------------------------------------------------------------------------------------------------------------------------------------------------
// ------------------------------------------------------------------------------------------------------------------------------------------------------------------------------
// ------------------------------------------------------------------------------------------------------------------------------------------------------------------------------
// ------------------------------------------------------------------------------------------------------------------------------------------------------------------------------
// ------------------------------------------------------------------------------------------------------------------------------------------------------------------------------
